import React, { useContext, useState, useRef, useCallback } from 'react';
import { FetchContext } from '../../features/contexts/fetch';
import { AxiosInstance } from 'axios';
import {
  Box,
  Checkbox,
  Grid,
  Paper,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';

enum Operation {
  Brand    = 0,
  Features = 1,
  Specs    = 2
}

enum Brand {
  JBC        = 0,
  SNAP       = 1,
  HOF        = 2,
  GS         = 3,
  JBC_Value  = 10,
  SNAP_Value = 11,
  HOF_Value  = 12,
  GS_Value   = 13
}

type FeatureMask = number;

interface YearMonth {
  month: number;
  year: number;
}

interface SecurityData {
  isPrior34: boolean;
  isV75: boolean;
  includeVAG: boolean;
  serial: string;
  key: string;
  operation: Operation;
  releaseDate: YearMonth;
  featureDisabled: boolean;
  featureMask: FeatureMask;
  brand: Brand;
}

interface SecurityCode {
  description: string;
  code: string;
}

interface LegacySecurityData {
  prior34: boolean;
  v75: boolean;
  vag: boolean;
  serial: string;
  key: string;
  operation: number;
  month: number;
  year: number;
  featureOff: boolean;
  feature: number;
  brand: number;
}

function toLegacyPayload(data: SecurityData): LegacySecurityData {
  return {
    prior34: data.isPrior34,
    v75: data.isV75,
    vag: data.includeVAG,
    serial: data.serial,
    key: data.key,
    operation: data.operation,
    month: data.releaseDate.month,
    year: data.releaseDate.year,
    featureOff: data.featureDisabled,
    feature: data.featureMask,
    brand: data.brand,
  };
}

const initialSecurityData: SecurityData = {
  isPrior34: false,
  isV75: true,
  includeVAG: false,
  serial: '',
  key: 'AA0AA0000',
  operation: Operation.Brand,
  releaseDate: {
    month: 1 as const,
    year: new Date().getFullYear(),
  },
  featureDisabled: false,
  featureMask: 0,
  brand: Brand.JBC,
};

const Keygen: React.FC = (): React.JSX.Element => {
  const [securityData, setSecurityData] = useState<SecurityData>(initialSecurityData);
  const [securityCode, setSecurityCode] = useState<SecurityCode>({ description: '', code: '' });

  const serialRef = useRef<HTMLInputElement>(null);
  const keyRef = useRef<HTMLInputElement>(null);

  type BooleanKey = Extract<keyof SecurityData,
    'isPrior34' | 'isV75' | 'includeVAG' | 'featureDisabled'>;

  const toggleFlag = useCallback(
    (field: BooleanKey) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSecurityData(prev => ({
          ...prev,
          [field]: e.target.checked,
        }));
      },
    []
  );

  type StringKey = Extract<keyof SecurityData, 'serial' | 'key'>;

  const updateString = useCallback(
    (field: StringKey) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = field === 'key' ? e.target.value.toUpperCase() : e.target.value;
        setSecurityData(prev => ({
          ...prev,
          [field]: value,
        }));
      },
    []
  );

  const updateReleaseDate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const num = parseInt(e.target.value, 10)
      if (isNaN(num)) return
      setSecurityData(prev => ({
        ...prev,
        releaseDate: {
          ...prev.releaseDate,
          [e.target.id]: num
        }
      }))
    },
    []
  )

  const features = useRef([
    { id: 'Platinum' as const, mask: 0x0001, label: ' 3-20726AD1 • Platinum' },
    { id: 'Voice' as const, mask: 0x0002, label: ' 3-23926AD1 • Voice' },
    { id: 'Mercedes' as const, mask: 0x0004, label: ' 3-37426AD1 • Mercedes' },
    { id: 'BMW' as const, mask: 0x0008, label: ' BMW • Only use to disable BMW' },
    { id: 'Peugeot' as const, mask: 0x0010, label: ' 3-49226AD1 • Peugeot Citroen PSA' },
    { id: 'Truck' as const, mask: 0x0020, label: ' 3-52926AD1 • Truck' },
    { id: 'Gold' as const, mask: 0x0040, label: ' 3-60926AD1 • Gold' },
    { id: 'VIN' as const, mask: 0x0080, label: ' 3-63626AD1 • VIN' },
    { id: 'Audit' as const, mask: 0x0100, label: ' 3-71926AD1 • Audit' },
    { id: 'WAO' as const, mask: 0x0200, label: ' 3-?????AD1 • Mercedes WAO'},
  ]).current;

  const brands = useRef([
    { id: Brand.JBC, name: 'JBC', label: ' 3-40026*D1 • JBC Silver' },
    { id: Brand.SNAP, name: 'SNAP', label: ' 3-40226*D1 • Snap-on Sun' },
    { id: Brand.HOF, name: 'HOF', label: ' 3-40126*D1 • HOF Silver' },
    { id: Brand.GS, name: 'GS', label: ' 3-40326*D1 • GS' },
    { id: Brand.JBC_Value, name: 'JBC Value', label: ' 3-40426*D1 • JBC Value' },
    { id: Brand.SNAP_Value,name: 'SNAP Value', label: ' 3-40526*D1 • Snap-on Sun Entry Level' },
    { id: Brand.HOF_Value, name: 'HOF Value', label: ' 3-40626*D1 • HOF Value' },
    { id: Brand.GS_Value, name: 'GS Value', label: ' 3-40726*D1 • GS Entry Level' },
  ]).current;

  const securityCodeRef = useRef<HTMLDivElement>(null);

  const fetchContext = useContext(FetchContext) as {
    authAxios?: AxiosInstance | null;
    isAdmin: boolean;
    canAdd: boolean;
    canCreateSecurityCodes: boolean;
    canManageUsers: boolean;
  };

  const getSecurityCode = useCallback(
    async (data: SecurityData) => {
      const legacy = toLegacyPayload(data);
      console.log('Posting payload →', legacy);
      try {
        if (!fetchContext.authAxios) {
          throw new Error('Authentication client is not available.');
        }
        const { status, data } = await fetchContext.authAxios.post(
          '/securitydata',
          legacy
        );

        if (status === 200) {
          setSecurityCode(data as SecurityCode);
        }
      } catch (err: any) {
        console.error('Error fetching security code:', err);
        setSecurityCode({ description: 'Error generating code', code: '---- ---- ---- ----' });
      }
    },
    [fetchContext]
  );

  const isKeyValid = securityData.key.length === 9 || securityData.key.length === 18;
  const canSubmit = isKeyValid && securityData.key.trim() !== '';

  const handleVersion = useCallback(
    (op: Operation) => async () => {
      if (!canSubmit) return;
      const payload = { ...securityData, operation: op };
      await getSecurityCode(payload);
      securityCodeRef.current?.scrollIntoView({ behavior: 'smooth' });
    },
    [securityData, getSecurityCode, canSubmit]
  );

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', }}>
      <Paper elevation={3} sx={{
        p: 4, borderRadius: 3, width: '93vw', maxWidth: '93vw',
        bgcolor: (theme) => theme.palette.background.paper,
      }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Keycode Generator
        </Typography>

        <Grid container spacing={3}>
          {/* System Info */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: (theme) => theme.palette.background.paper }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ mb: 2 }}>Enter System Info</FormLabel>

                <TextField
                  inputRef={serialRef}
                  id="serial"
                  label="Serial/Order Number"
                  size="small"
                  fullWidth
                  value={securityData.serial}
                  onChange={updateString('serial')}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'Merriweather, serif',
                      fontSize: '1.1em',
                    },
                  }}
                />

                <TextField
                  inputRef={keyRef}
                  id="key"
                  label="Key Number"
                  size="small"
                  fullWidth
                  value={securityData.key}
                  onChange={updateString('key')}
                  error={securityData.key.length > 0 && !isKeyValid}
                  helperText={securityData.key.length > 0 && !isKeyValid ? "Key must be 9 or 18 characters long" : ""}
                  inputProps={{ maxLength: 18, style: { textTransform: 'uppercase' } }}
                  sx={{ mt: 2,
                    '& .MuiInputBase-input': {
                      fontFamily: 'Merriweather, serif',
                      fontSize: '1.1em',
                    },
                  }}
                />
              </FormControl>
            </Paper>
          </Grid>

          {/* Version */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: (theme) => theme.palette.background.paper }}>
              <FormControl component="fieldset">
                <FormLabel sx={{ mb: 1 }}>Select Version</FormLabel>

                <FormGroup sx={{'& .MuiFormControlLabel-root': {marginTop: 0, marginBottom: 0,}}}>
                  <FormControlLabel control={
                    <Checkbox checked={securityData.isPrior34} onChange={toggleFlag('isPrior34')} />}
                    label="Prior to 3.4"
                  />
                  <FormControlLabel control={
                    <Checkbox checked={securityData.isV75} onChange={toggleFlag('isV75')} />}
                    label=">= Pro42 7.5.0"
                  />
                </FormGroup>
              </FormControl>
            </Paper>
          </Grid>

          {/* Generated Code */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: (theme) => theme.palette.background.paper }}
              ref={securityCodeRef}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ mb: 2 }}>Generated Security Code</FormLabel>
                <Typography variant="h5" sx={{ fontFamily: 'Merriweather, serif', textAlign: 'center', mt: 1, }}>
                  {securityCode.code || '---- ---- ---- ----'}
                </Typography>
              </FormControl>
            </Paper>
          </Grid>

          {/* Spacer */}
          <Grid item xs={12}>
            <Box sx={{ height: 8 }} />
          </Grid>

          {/* Brand */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: (theme) => theme.palette.background.paper }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ mb: 1 }}>Select Brand</FormLabel>

                <RadioGroup value={securityData.brand}
                  onChange={(e) =>
                    setSecurityData((prev) => ({
                      ...prev,
                      brand: Number(e.target.value),
                    }))
                  }
                >
                  {brands.map((b) => (
                    <FormControlLabel key={b.id} value={b.id} control={<Radio />} label={b.label} />
                  ))}
                </RadioGroup>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button variant="outlined" onClick={handleVersion(Operation.Brand)} disabled={!canSubmit}>
                    Generate Brand Code
                  </Button>
                </Box>
              </FormControl>
            </Paper>
          </Grid>

          {/* Features */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: (theme) => theme.palette.background.paper }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ mb: 1 }}>Select Features</FormLabel>

                <FormGroup>
                  {features.map((f) => (
                    <FormControlLabel
                      key={f.id}
                      control={
                        <Checkbox
                          checked={Boolean(securityData.featureMask & f.mask)}
                          onChange={(e) =>
                            setSecurityData((prev) => ({
                              ...prev,
                              featureMask: e.target.checked
                                ? prev.featureMask | f.mask
                                : prev.featureMask & ~f.mask,
                            }))
                          }
                        />
                      }
                      label={f.label}
                    />
                  ))}
                </FormGroup>

                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={securityData.featureDisabled}
                      onChange={toggleFlag('featureDisabled')}
                    />
                  }
                  label="Check to disable selected features"
                />

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleVersion(Operation.Features)}
                    disabled={!canSubmit}
                  >
                    Generate Feature Code
                  </Button>
                </Box>
              </FormControl>
            </Paper>
          </Grid>

          {/* Specifications */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: (theme) => theme.palette.background.paper }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel sx={{ mb: 1 }}>Specifications</FormLabel>

                <FormLabel sx={{ mt: 1 }}>Release Date</FormLabel>

                <Box sx={{ mt: 1 }}>
                  <TextField
                    type="number"
                    id="month"
                    label="Month"
                    size="small"
                    inputProps={{ min: 1, max: 12 }}
                    value={securityData.releaseDate.month}
                    onChange={updateReleaseDate}
                    sx={{ width: 100, mr: 1 }}
                  />

                  <TextField
                    type="number"
                    id="year"
                    label="Year"
                    size="small"
                    value={securityData.releaseDate.year}
                    onChange={updateReleaseDate}
                    sx={{ width: 100 }}
                  />
                </Box>

                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox
                      checked={securityData.includeVAG}
                      onChange={toggleFlag('includeVAG')}
                    />
                  }
                  label="Check for VAG Specs"
                />

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleVersion(Operation.Specs)}
                    disabled={!canSubmit}
                  >
                    Generate Specification Code
                  </Button>
                </Box>
              </FormControl>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Keygen;